/**
 * Register Page - עמוד הרשמה
 * Complete user registration with GDPR compliance
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { UserPlus, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import PasswordStrengthIndicator from '../components/auth/PasswordStrengthIndicator'
import api, { handleApiError } from '../services/api'


// Registration validation schema (must match backend)
const registerSchema = z.object({
  email: z
    .string()
    .email('כתובת אימייל לא תקינה')
    .min(1, 'שדה חובה'),
  password: z
    .string()
    .min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים')
    .regex(/[A-Za-z֐-׿]/, 'הסיסמה חייבת להכיל לפחות אות אחת')
    .regex(/[0-9]/, 'הסיסמה חייבת להכיל לפחות ספרה אחת'),
  confirmPassword: z.string().min(1, 'שדה חובה'),
  firstName: z
    .string()
    .min(2, 'שם פרטי חייב להכיל לפחות 2 תווים')
    .max(50, 'שם פרטי ארוך מדי'),
  lastName: z
    .string()
    .min(2, 'שם משפחה חייב להכיל לפחות 2 תווים')
    .max(50, 'שם משפחה ארוך מדי'),
  phoneNumber: z
    .string()
    .regex(/^0[2-9]\d{7,8}$/, 'מספר טלפון לא תקין (דוגמה: 0501234567)')
    .optional()
    .or(z.literal('')),
  nationalId: z
    .string()
    .regex(/^\d{9}$/, 'תעודת זהות חייבת להכיל 9 ספרות')
    .optional()
    .or(z.literal('')),
  dateOfBirth: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'תאריך לא תקין')
    .optional()
    .or(z.literal('')),
  gdprConsent: z.object({
    dataProcessing: z.boolean().refine((val) => val === true, {
      message: 'חובה לאשר עיבוד נתונים להמשך ההרשמה'
    }),
    marketingEmails: z.boolean()
  }),
  privacyPolicyAccepted: z.boolean().refine((val) => val === true, {
    message: 'חובה לקבל את תנאי השימוש ומדיניות הפרטיות'
  })
}).refine((data) => data.password === data.confirmPassword, {
  message: 'הסיסמאות אינן תואמות',
  path: ['confirmPassword'],
})

type RegisterFormData = z.infer<typeof registerSchema>

export default function RegisterPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      gdprConsent: {
        dataProcessing: false,
        marketingEmails: false,
      },
      privacyPolicyAccepted: false,
    },
  })

  const password = watch('password')

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Prepare registration data (matches backend schema)
      const registerData = {
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || undefined,
        nationalId: data.nationalId || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
        gdprConsent: data.gdprConsent,
        privacyPolicyAccepted: data.privacyPolicyAccepted,
      }

      await api.post('/auth/register', registerData)

      // Show "check your email" screen — user must verify before they can log in
      setSuccess(true)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  // Success screen — verify email via link before logging in
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              ההרשמה הושלמה בהצלחה!
            </h2>
            <p className="text-gray-600 mb-2">
              שלחנו אליך אימייל לאימות כתובת הדוא"ל.
            </p>
            <p className="text-gray-600 mb-4">
              אנא בדוק את תיבת הדואר שלך (כולל תיקיית הספאם) ולחץ על הקישור לאימות החשבון.
            </p>
            <p className="text-sm text-gray-500">
              לאחר אימות החשבון תוכל/י להתחבר למערכת.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-2xl my-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary-600 mb-2">
            יפתח ונגר יועץ השקעות
          </h1>
          <p className="text-gray-600">אזור אישי - הרשמה</p>
        </div>

        {/* Registration Form */}
        <div className="card">
          <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">
            הרשמה לחשבון חדש
          </h2>

          {/* Error Alert */}
          {error && (
            <div className="alert alert-error mb-6" role="alert">
              <AlertCircle size={20} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Personal Information */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-heading font-semibold text-gray-900 mb-4">
                פרטים אישיים
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    שם פרטי <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('firstName')}
                    type="text"
                    id="firstName"
                    className={`input ${errors.firstName ? 'input-error' : ''}`}
                    placeholder="יפתח"
                    aria-required="true"
                    aria-invalid={!!errors.firstName}
                    aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                  />
                  {errors.firstName && (
                    <p id="firstName-error" role="alert" className="text-sm text-red-600 mt-1">{errors.firstName.message}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    שם משפחה <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('lastName')}
                    type="text"
                    id="lastName"
                    className={`input ${errors.lastName ? 'input-error' : ''}`}
                    placeholder="ונגר"
                    aria-required="true"
                    aria-invalid={!!errors.lastName}
                    aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                  />
                  {errors.lastName && (
                    <p id="lastName-error" role="alert" className="text-sm text-red-600 mt-1">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="mt-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  אימייל <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  placeholder="example@email.com"
                  autoComplete="email"
                  dir="ltr"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" role="alert" className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="mt-4">
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  טלפון נייד
                </label>
                <input
                  {...register('phoneNumber')}
                  type="tel"
                  id="phoneNumber"
                  className={`input ${errors.phoneNumber ? 'input-error' : ''}`}
                  placeholder="0501234567"
                  dir="ltr"
                  aria-invalid={!!errors.phoneNumber}
                  aria-describedby={errors.phoneNumber ? 'phoneNumber-error' : undefined}
                />
                {errors.phoneNumber && (
                  <p id="phoneNumber-error" role="alert" className="text-sm text-red-600 mt-1">{errors.phoneNumber.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* National ID */}
                <div>
                  <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700 mb-2">
                    תעודת זהות
                  </label>
                  <input
                    {...register('nationalId')}
                    type="text"
                    id="nationalId"
                    className={`input ${errors.nationalId ? 'input-error' : ''}`}
                    placeholder="123456789"
                    dir="ltr"
                    maxLength={9}
                    aria-invalid={!!errors.nationalId}
                    aria-describedby={errors.nationalId ? 'nationalId-error' : undefined}
                  />
                  {errors.nationalId && (
                    <p id="nationalId-error" role="alert" className="text-sm text-red-600 mt-1">{errors.nationalId.message}</p>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                    תאריך לידה
                  </label>
                  <input
                    {...register('dateOfBirth')}
                    type="date"
                    id="dateOfBirth"
                    className={`input ${errors.dateOfBirth ? 'input-error' : ''}`}
                    aria-invalid={!!errors.dateOfBirth}
                    aria-describedby={errors.dateOfBirth ? 'dateOfBirth-error' : undefined}
                  />
                  {errors.dateOfBirth && (
                    <p id="dateOfBirth-error" role="alert" className="text-sm text-red-600 mt-1">{errors.dateOfBirth.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Password Section */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-heading font-semibold text-gray-900 mb-4">
                סיסמה
              </h3>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  סיסמה <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    className={`input ${errors.password ? 'input-error' : ''}`}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    dir="ltr"
                    aria-required="true"
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? 'הסתרת סיסמה' : 'הצגת סיסמה'}
                  >
                    {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" role="alert" className="text-sm text-red-600 mt-1">{errors.password.message}</p>
                )}

                {/* Password Strength Indicator */}
                <PasswordStrengthIndicator password={password || ''} />
              </div>

              {/* Confirm Password */}
              <div className="mt-4">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  אימות סיסמה <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword')}
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    dir="ltr"
                    aria-required="true"
                    aria-invalid={!!errors.confirmPassword}
                    aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showConfirmPassword ? 'הסתרת סיסמה' : 'הצגת סיסמה'}
                  >
                    {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p id="confirmPassword-error" role="alert" className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            {/* GDPR Consent */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-heading font-semibold text-gray-900 mb-4">
                הסכמות ומדיניות פרטיות (GDPR)
              </h3>

              {/* Privacy Policy Consent (Required) */}
              <div className="flex items-start gap-3 mb-4">
                <input
                  {...register('privacyPolicyAccepted')}
                  type="checkbox"
                  id="privacyPolicyAccepted"
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  aria-required="true"
                  aria-invalid={!!errors.privacyPolicyAccepted}
                  aria-describedby={errors.privacyPolicyAccepted ? 'privacyPolicyAccepted-error' : undefined}
                />
                <label htmlFor="privacyPolicyAccepted" className="text-sm text-gray-700 flex-1">
                  <span className="text-red-500">*</span> אני מאשר/ת שקראתי והסכמתי ל
                  <Link to="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 underline mx-1">
                    תקנון האתר ותנאי השימוש
                  </Link>
                  ול
                  <Link to="/privacy" target="_blank" className="text-primary-600 hover:text-primary-700 underline mx-1">
                    מדיניות הפרטיות
                  </Link>
                  של יפתח ונגר יועץ השקעות.
                </label>
              </div>
              {errors.privacyPolicyAccepted && (
                <p id="privacyPolicyAccepted-error" role="alert" className="text-sm text-red-600 mb-4">{errors.privacyPolicyAccepted.message}</p>
              )}

              {/* Data Processing Consent (Required) */}
              <div className="flex items-start gap-3 mb-4">
                <input
                  {...register('gdprConsent.dataProcessing')}
                  type="checkbox"
                  id="dataProcessing"
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  aria-required="true"
                  aria-invalid={!!errors.gdprConsent?.dataProcessing}
                  aria-describedby={errors.gdprConsent?.dataProcessing ? 'dataProcessing-error' : undefined}
                />
                <label htmlFor="dataProcessing" className="text-sm text-gray-700 flex-1">
                  <span className="text-red-500">*</span> אני מאשר/ת לעיבוד הנתונים האישיים שלי בהתאם ל-GDPR וחוקי הפרטיות בישראל.
                </label>
              </div>
              {errors.gdprConsent?.dataProcessing && (
                <p id="dataProcessing-error" role="alert" className="text-sm text-red-600 mb-4">{errors.gdprConsent.dataProcessing.message}</p>
              )}

              {/* Marketing Consent (Optional) */}
              <div className="flex items-start gap-3">
                <input
                  {...register('gdprConsent.marketingEmails')}
                  type="checkbox"
                  id="marketingEmails"
                  className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="marketingEmails" className="text-sm text-gray-700 flex-1">
                  אני מאשר/ת לקבל עדכונים שיווקיים, ניוזלטרים והמלצות השקעה באימייל (ניתן לבטל בכל עת)
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner" />
                  <span>מבצע הרשמה...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <UserPlus size={20} aria-hidden="true" />
                  <span>הירשם</span>
                </div>
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              כבר יש לך חשבון?{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
                התחבר כאן
              </Link>
            </p>
            <p className="text-gray-600 mt-2">
              תלמיד/ה בתוכנית בית ספרית?{' '}
              <Link to="/register-student" className="text-primary-600 hover:text-primary-700 font-medium">
                הרשמה דרך קוד כיתה
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>יפתח ונגר יועץ השקעות</p>
          <p className="mt-1">© 2026 כל הזכויות שמורות</p>
        </div>
      </div>
    </div>
  )
}
